import { Grid, Stack } from '@mui/material';
import { APP_PAGE_SIZE_MEDIUM } from 'app/appConstants';
import { AxiosResponse } from 'axios';
import { MprcCardActions, MprcContainer, MprcListItemTitle, MprcSpinnerIndicator } from 'components';
import { MprcFilterCount } from 'components/filter';
import { GetGeneralUnitsPayload, TrainingIndexUnitDtoPaginatedList } from 'generated/CoreApi_v1';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashPagination } from 'views/dashboard/components/DashPagination';
import { TrainingIndexResult } from 'views/dashboard/TrainingIndex/components/TrainingIndexResult';
import { TrainingIndexUnitsFilter } from 'views/dashboard/TrainingIndex/components/TrainingIndexUnits';
import { TrainingIndexProps } from 'views/dashboard/TrainingIndex/TrainingIndexRoot';

////////////////////////////////////////////////////////////////////////////

interface TrainingIndexUnitsFlatListFilter extends TrainingIndexUnitsFilter {
    pageNumber: number;
}

interface IProps extends TrainingIndexProps {
    filter: TrainingIndexUnitsFilter;
    getApi: (data: GetGeneralUnitsPayload) => Promise<AxiosResponse<TrainingIndexUnitDtoPaginatedList>>;
    onError: (e: any) => void;
    onDrillDownItem: (unitId: string, name: string) => void;
    onResetSearch: () => void;
    onExportData?: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const TrainingIndexUnitsFlatList: React.FC<IProps> = ({ currentMonth, onError, getApi, onDrillDownItem, onExportData, onResetSearch, ...props }) => {

    const { t } = useTranslation("dashboard");

    const [loading, setLoading] = useState<boolean>(false);
    const [viewData, setViewData] = useState<TrainingIndexUnitDtoPaginatedList>();
    const [filter, setFilter] = useState<TrainingIndexUnitsFlatListFilter>({ ...props.filter, pageNumber: 1 });

    useEffect(() => {
        setLoading(true);
        getApi({
            yearMonth: currentMonth,
            searchText: filter.searchText,
            orderBy: filter.orderBy,
            pageNumber: filter.pageNumber,
            pageSize: APP_PAGE_SIZE_MEDIUM,
            onlyDeleted: filter.onlyDeleted
        })
            .then(response => setViewData(response.data))
            .catch(e => onError(e))
            .finally(() => setLoading(false));
    }, [filter, currentMonth]);

    useEffect(() => {
        setFilter({ ...props.filter, pageNumber: 1 });
    }, [props.filter]);

    return loading ?
        <MprcSpinnerIndicator />
        : <Stack>
            {viewData &&
                <>
                    <MprcContainer>
                        <MprcFilterCount
                            loading={loading}
                            isEmpty={viewData.totalRows == 0}
                            countText={t("TrainingIndex.UnitsRecordsFound", { count: viewData.totalRows })}
                            filter={{
                                active: filter.searchText.length > 0,
                                text: t("TrainingIndex.UnitsRecordsFiltered", { count: viewData.totalRows }),
                                onResetFilter: onResetSearch
                            }}
                        />
                    </MprcContainer>
                    <MprcContainer>
                        {viewData.items.map(item =>
                            <Grid key={item.unitId} container justifyContent={"space-between"}>
                                <Grid>
                                    <MprcListItemTitle onClick={() => onDrillDownItem(item.unitId, item.name)} color={item.isDeleted ? "warning" : undefined}>{item.name}</MprcListItemTitle>
                                </Grid>
                                <Grid>
                                    <TrainingIndexResult value={item.trainingIndex} onClick={() => onDrillDownItem(item.unitId, item.name)} />
                                </Grid>
                            </Grid>
                        )}
                    </MprcContainer>
                    {!loading && viewData &&
                        <MprcCardActions>
                            <DashPagination
                                pageNumber={viewData.pageNumber}
                                totalPages={viewData.totalPages}
                                onSetPageNumber={(page) => setFilter(state => ({ ...state, pageNumber: page }))}
                                onExportData={onExportData}
                            />
                        </MprcCardActions>
                    }
                </>
            }
        </Stack>
        ;
};
